<template>
  <div class="training-plan-container" :key="$i18n.locale">

    <!-- Header Section -->
    <div class="header-section">
      <div class="container">
        <div class="header-content">
          <h1 class="page-title">
            <font-awesome-icon icon="dumbbell" class="title-icon" />
            {{ $t('trainingPlan.title') }}
          </h1>
          <p class="page-subtitle">
            {{ $t('trainingPlan.subtitle') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Plan Selection Section -->
    <div class="plans-section">
      <div class="container">
        <!-- Plan Cards Grid -->
        <div class="plans-grid" v-if="!selectedPlan">
          <div v-for="(plan, index) in trainingPlans" :key="index" class="plan-card"
            :class="{ 'plan-card--featured': index === 0 }" @click="selectPlan(plan)">
            <div class="plan-card__header">
              <div class="plan-card__icon">
                <font-awesome-icon :icon="getPlanIcon(plan.distance)" />
              </div>
              <h3 class="plan-card__title">{{ plan.name }}</h3>
              <p class="plan-card__description">{{ plan.description }}</p>
            </div>

            <div class="plan-card__stats">
              <div class="stat-item">
                <font-awesome-icon icon="calendar" class="stat-icon" />
                <div class="stat-content">
                  <span class="stat-value">{{ plan.duration }}</span>
                  <span class="stat-label">{{ $t('trainingPlan.weeks') }}</span>
                </div>
              </div>
              <div class="stat-item">
                <font-awesome-icon icon="road" class="stat-icon" />
                <div class="stat-content">
                  <span class="stat-value">{{ plan.distance }}</span>
                  <span class="stat-label">{{ $t('trainingPlan.distance') }}</span>
                </div>
              </div>
              <div class="stat-item">
                <font-awesome-icon icon="clock" class="stat-icon" />
                <div class="stat-content">
                  <span class="stat-value">{{ plan.targetTime }}</span>
                  <span class="stat-label">{{ $t('trainingPlan.targetTime') }}</span>
                </div>
              </div>
            </div>

            <div class="plan-card__footer">
              <button class="btn btn-primary btn-block">
                <font-awesome-icon icon="play-circle" />
                {{ $t('trainingPlan.startPlan') }}
              </button>

              <!-- Goal Status for Plan Card -->
              <div v-if="planGoals[plan.id]" class="plan-goal-status">
                <div class="plan-goal-indicator">
                  <font-awesome-icon icon="bullseye" />
                  <span class="plan-goal-time">{{ planGoals[plan.id].targetTime }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Plan Detail View -->
        <div v-if="selectedPlan" class="plan-detail">
          <!-- Back Button -->
          <div class="back-section">
            <button @click="selectedPlan = null" class="btn btn-outline-secondary">
              <font-awesome-icon icon="arrow-left" />
              {{ $t('trainingPlan.backToPlans') }}
            </button>
          </div>

          <!-- Plan Overview -->
          <div class="plan-overview">
            <div class="plan-overview__header">
              <div class="plan-overview__icon">
                <font-awesome-icon :icon="getPlanIcon(selectedPlan.distance)" />
              </div>
              <div class="plan-overview__info">
                <h2 class="plan-overview__title">{{ selectedPlan.name }}</h2>
                <p class="plan-overview__description">{{ selectedPlan.description }}</p>
                <div class="plan-overview__meta">
                  <span class="meta-item">
                    <font-awesome-icon icon="calendar" />
                    {{ selectedPlan.duration }} {{ $t('trainingPlan.weeks') }}
                  </span>
                  <span class="meta-item">
                    <font-awesome-icon icon="road" />
                    {{ selectedPlan.distance }}
                  </span>
                  <span class="meta-item">
                    <font-awesome-icon icon="clock" />
                    {{ selectedPlan.targetTime }}
                  </span>
                </div>
              </div>
            </div>

          </div>

          <!-- Weekly Schedule -->
          <div v-if="showSchedule" class="schedule-section">
            <!-- Mobile Back Button -->
            <div class="mobile-back-btn">
              <button @click="closeScheduleView" class="btn btn-outline-secondary mobile-back-button">
                <font-awesome-icon icon="arrow-left" />
                {{ $t('trainingPlan.backToPlans') }}
              </button>
            </div>

            <h3 class="section-title">
              <font-awesome-icon icon="calendar-alt" />
              {{ $t('trainingPlan.weeklySchedule') }}
              <button @click="closeScheduleView" class="schedule-close-btn" title="Close">
                <font-awesome-icon icon="times" />
              </button>
            </h3>

            <div v-if="selectedWeek" class="week-schedule">
              <div class="week-header">
                <h4 class="week-title">
                  {{ $t('trainingPlan.week') }} {{ selectedWeek.week }}: {{ selectedWeek.focus }}
                </h4>
                <div class="week-stats">
                  <span class="week-stat">
                    <font-awesome-icon icon="road" />
                    {{ selectedWeek.totalDistance }}
                  </span>
                </div>
              </div>

              <!-- Action Section -->
              <div class="action-section">
                <div class="action-header">
                  <h3 class="action-title">
                    <font-awesome-icon icon="rocket" />
                    {{ $t('trainingPlan.readyToStart') }}
                  </h3>
                  <p class="action-subtitle">
                    {{ $t('trainingPlan.actionSubtitle') }}
                  </p>
                </div>

                <div class="action-buttons">
                  <div class="action-button-group">
                    <button class="action-btn action-btn--primary" @click="startTraining">
                      <div class="action-btn__icon">
                        <font-awesome-icon icon="play-circle" />
                      </div>
                      <div class="action-btn__content">
                        <span class="action-btn__title">{{ $t('trainingPlan.startTraining') }}</span>
                        <span class="action-btn__subtitle">{{ $t('trainingPlan.startTrainingDesc') }}</span>
                      </div>
                      <div class="action-btn__arrow">
                        <font-awesome-icon icon="arrow-right" />
                      </div>
                    </button>

                    <button @click="showGoalModal = true" class="action-btn action-btn--secondary">
                      <div class="action-btn__icon">
                        <font-awesome-icon icon="bullseye" />
                      </div>
                      <div class="action-btn__content">
                        <span class="action-btn__title">{{ $t('trainingPlan.setGoal') }}</span>
                        <span class="action-btn__subtitle">{{ $t('trainingPlan.setGoalDesc') }}</span>
                      </div>
                      <div class="action-btn__arrow">
                        <font-awesome-icon icon="arrow-right" />
                      </div>
                    </button>
                  </div>

                  <div class="action-button-group">
                    <button @click="downloadTrainingPlan" class="action-btn action-btn--tertiary">
                      <div class="action-btn__icon">
                        <font-awesome-icon icon="download" />
                      </div>
                      <div class="action-btn__content">
                        <span class="action-btn__title">{{ $t('trainingPlan.downloadPlan') }}</span>
                        <span class="action-btn__subtitle">{{ $t('trainingPlan.downloadPlanDesc') }}</span>
                      </div>
                    </button>

                    <button @click="sharePlan" class="action-btn action-btn--tertiary">
                      <div class="action-btn__icon">
                        <font-awesome-icon icon="share" />
                      </div>
                      <div class="action-btn__content">
                        <span class="action-btn__title">{{ $t('trainingPlan.sharePlan') }}</span>
                        <span class="action-btn__subtitle">{{ $t('trainingPlan.sharePlanDesc') }}</span>
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Goal Status Indicator -->
                <div v-if="currentGoal" class="goal-status">
                  <div class="goal-status__icon">
                    <font-awesome-icon icon="check-circle" />
                  </div>
                  <div class="goal-status__content">
                    <span class="goal-status__title">{{ $t('trainingPlan.goalSet') }} - {{ selectedPlan.name }}</span>
                    <span class="goal-status__time">{{ currentGoal.targetTime }}</span>
                                          <span v-if="calculateRacePace()" class="goal-status__pace">{{ $t('trainingPlan.targetPace') }}: {{ calculateRacePace() }}/km</span>
                  </div>
                  <button @click="showGoalModal = true" class="goal-status__edit">
                    <font-awesome-icon icon="edit" />
                  </button>
                </div>
              </div>

              <div class="week-selector">
                <div class="week-buttons">
                  <button
                    v-for="week in adjustedTrainingPlan.weeks"
                    :key="week.week"
                    @click="selectWeek(week)"
                    class="week-button"
                    :class="{ 'week-button--active': selectedWeek && selectedWeek.week === week.week }"
                    :title="`${$t('trainingPlan.week')} ${week.week}: ${week.focus}`"
                  >
                    <font-awesome-icon icon="calendar-week" class="week-icon" />
                    <span class="week-number">W{{ week.week }}</span>
                  </button>
                </div>
              </div>

              <!-- Training Schedule Table -->
              <div class="training-table-container">
                <!-- Desktop Table View -->
                <table class="training-table desktop-table">
                  <thead>
                    <tr>
                      <th class="table-header-day">Day</th>
                      <th class="table-header-type">Type</th>
                      <th class="table-header-description">Description</th>
                      <th class="table-header-metrics">Metrics</th>
                      <th class="table-header-intensity">Intensity</th>
                      <th class="table-header-notes">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(day, dayName) in getDaysOfWeek()" :key="dayName" class="table-row"
                      :class="`table-row--${dayName}`">
                      <!-- Day Column -->
                      <td class="table-cell-day">
                        <div class="day-info">
                          <div class="day-name">{{ $t(`trainingPlan.days.${dayName}`) }}</div>
                        </div>
                      </td>

                      <!-- Type Column -->
                      <td class="table-cell-type">
                        <div v-if="!isRestDay(adjustedSelectedWeek[dayName])" class="session-type-badge">
                          {{ adjustedSelectedWeek[dayName].type }}
                        </div>
                        <div v-else class="rest-badge" :class="{ 'rest-badge--cross-training': adjustedSelectedWeek[dayName].type === 'Rest/Cross-Training' }">
                          <font-awesome-icon icon="bed" />
                          {{ adjustedSelectedWeek[dayName].type === 'Rest/Cross-Training' ? 'Rest/Cross' : 'Rest' }}
                        </div>
                      </td>

                      <!-- Description Column -->
                      <td class="table-cell-description">
                        <div v-if="!isRestDay(adjustedSelectedWeek[dayName])" class="session-description">
                          {{ adjustedSelectedWeek[dayName].description }}
                        </div>
                        <div v-else class="rest-description">
                          {{ adjustedSelectedWeek[dayName].description || $t('trainingPlan.recoveryAndRestDay') }}
                        </div>
                      </td>

                      <!-- Metrics Column -->
                      <td class="table-cell-metrics">
                        <div v-if="!isRestDay(adjustedSelectedWeek[dayName])" class="session-metrics">
                          <div class="metric-item" v-if="adjustedSelectedWeek[dayName].metrics && adjustedSelectedWeek[dayName].metrics.distance">
                            <font-awesome-icon icon="road" />
                            <span :class="{ 'adjusted-metric': currentGoal }">{{ adjustedSelectedWeek[dayName].metrics.distance }}</span>
                          </div>
                          <div class="metric-item" v-if="adjustedSelectedWeek[dayName].metrics && adjustedSelectedWeek[dayName].metrics.pace">
                            <font-awesome-icon icon="tachometer-alt" />
                            <span :class="{ 'adjusted-metric': currentGoal }">{{ adjustedSelectedWeek[dayName].metrics.pace }}</span>
                          </div>
                          <div class="metric-item" v-if="adjustedSelectedWeek[dayName].metrics && adjustedSelectedWeek[dayName].metrics.duration">
                            <font-awesome-icon icon="clock" />
                            <span :class="{ 'adjusted-metric': currentGoal }">{{ adjustedSelectedWeek[dayName].metrics.duration }}</span>
                          </div>
                          <div v-if="currentGoal && adjustedSelectedWeek[dayName].metrics && (adjustedSelectedWeek[dayName].metrics.distance || adjustedSelectedWeek[dayName].metrics.pace)" class="metric-adjustment-indicator">
                            <font-awesome-icon icon="bullseye" />
                            <span>{{ $t('trainingPlan.goalAdjusted') }}</span>
                          </div>
                        </div>
                        <div v-else class="rest-metrics">
                          <span class="rest-text">{{ adjustedSelectedWeek[dayName].metrics && adjustedSelectedWeek[dayName].metrics.duration ? adjustedSelectedWeek[dayName].metrics.duration : $t('trainingPlan.noTraining') }}</span>
                        </div>
                      </td>

                      <!-- Intensity Column -->
                      <td class="table-cell-intensity">
                        <div v-if="!isRestDay(adjustedSelectedWeek[dayName])" class="intensity-badge"
                          :class="`intensity-badge--${adjustedSelectedWeek[dayName].intensity.toLowerCase()}`">
                          {{ adjustedSelectedWeek[dayName].intensity }}
                        </div>
                        <div v-else class="rest-intensity">
                          <span class="rest-text">{{ adjustedSelectedWeek[dayName].intensity || 'Rest' }}</span>
                        </div>
                      </td>

                      <!-- Notes Column -->
                      <td class="table-cell-notes">
                        <div v-if="adjustedSelectedWeek[dayName].notes" class="session-notes">
                          <font-awesome-icon icon="info-circle" />
                          <span>{{ adjustedSelectedWeek[dayName].notes }}</span>
                        </div>
                        <div v-else class="no-notes">
                          <span class="no-notes-text">-</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Mobile Card View -->
                <div class="mobile-training-cards">
                  <div v-for="(day, dayName) in getDaysOfWeek()" :key="dayName" class="training-day-card"
                    :class="`training-day-card--${dayName}`">
                    <div class="day-card-header">
                      <div class="day-card-title">
                        <font-awesome-icon icon="calendar-day" class="day-icon" />
                        {{ $t(`trainingPlan.days.${dayName}`) }}
                      </div>
                      <div class="day-card-type">
                        <div v-if="!isRestDay(adjustedSelectedWeek[dayName])" class="session-type-badge mobile-badge">
                          {{ adjustedSelectedWeek[dayName].type }}
                        </div>
                        <div v-else class="rest-badge mobile-badge" :class="{ 'rest-badge--cross-training': adjustedSelectedWeek[dayName].type === 'Rest/Cross-Training' }">
                          <font-awesome-icon icon="bed" />
                          {{ adjustedSelectedWeek[dayName].type === 'Rest/Cross-Training' ? 'Rest/Cross' : 'Rest' }}
                        </div>
                      </div>
                    </div>

                    <div class="day-card-content">
                      <div class="day-card-description">
                        <div v-if="!isRestDay(adjustedSelectedWeek[dayName])" class="session-description">
                          {{ adjustedSelectedWeek[dayName].description }}
                        </div>
                        <div v-else class="rest-description">
                          {{ adjustedSelectedWeek[dayName].description || $t('trainingPlan.recoveryAndRestDay') }}
                        </div>
                      </div>

                      <div class="day-card-metrics">
                        <div v-if="!isRestDay(adjustedSelectedWeek[dayName])" class="session-metrics mobile-metrics">
                          <div class="metric-item" v-if="adjustedSelectedWeek[dayName].metrics && adjustedSelectedWeek[dayName].metrics.distance">
                            <font-awesome-icon icon="road" />
                            <span :class="{ 'adjusted-metric': currentGoal }">{{ adjustedSelectedWeek[dayName].metrics.distance }}</span>
                          </div>
                          <div class="metric-item" v-if="adjustedSelectedWeek[dayName].metrics && adjustedSelectedWeek[dayName].metrics.pace">
                            <font-awesome-icon icon="tachometer-alt" />
                            <span :class="{ 'adjusted-metric': currentGoal }">{{ adjustedSelectedWeek[dayName].metrics.pace }}</span>
                          </div>
                          <div class="metric-item" v-if="adjustedSelectedWeek[dayName].metrics && adjustedSelectedWeek[dayName].metrics.duration">
                            <font-awesome-icon icon="clock" />
                            <span :class="{ 'adjusted-metric': currentGoal }">{{ adjustedSelectedWeek[dayName].metrics.duration }}</span>
                          </div>
                          <div v-if="currentGoal && adjustedSelectedWeek[dayName].metrics && (adjustedSelectedWeek[dayName].metrics.distance || adjustedSelectedWeek[dayName].metrics.pace)" class="metric-adjustment-indicator">
                            <font-awesome-icon icon="bullseye" />
                            <span>{{ $t('trainingPlan.goalAdjusted') }}</span>
                          </div>
                        </div>
                        <div v-else class="rest-metrics">
                          <span class="rest-text">{{ adjustedSelectedWeek[dayName].metrics && adjustedSelectedWeek[dayName].metrics.duration ? adjustedSelectedWeek[dayName].metrics.duration : $t('trainingPlan.noTraining') }}</span>
                        </div>
                      </div>

                      <div class="day-card-footer">
                        <div class="day-card-intensity">
                          <div v-if="!isRestDay(adjustedSelectedWeek[dayName])" class="intensity-badge mobile-intensity"
                            :class="`intensity-badge--${adjustedSelectedWeek[dayName].intensity.toLowerCase()}`">
                            {{ adjustedSelectedWeek[dayName].intensity }}
                          </div>
                          <div v-else class="rest-intensity">
                            <span class="rest-text">{{ adjustedSelectedWeek[dayName].intensity || 'Rest' }}</span>
                          </div>
                        </div>

                        <div v-if="adjustedSelectedWeek[dayName].notes" class="day-card-notes">
                          <font-awesome-icon icon="info-circle" />
                          <span>{{ adjustedSelectedWeek[dayName].notes }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Goal Setting Modal -->
          <div v-if="showGoalModal" class="modal-overlay" @click="showGoalModal = false">
            <div class="modal-content" @click.stop>
              <div class="modal-header">
                <h3 class="modal-title">
                  <font-awesome-icon icon="bullseye" />
                  {{ $t('trainingPlan.setRaceGoal') }} - {{ selectedPlan.name }}
                </h3>
                <button @click="showGoalModal = false" class="modal-close">
                  <font-awesome-icon icon="times" />
                </button>
              </div>

              <div class="modal-body">
                <div class="goal-form">
                  <div class="goal-description">
                    <p>{{ $t('trainingPlan.goalDescription') }}</p>
                  </div>

                  <div class="goal-input-section">
                    <label for="goal-hours" class="goal-label">
                      {{ $t('trainingPlan.targetHours') }}:
                    </label>
                    <div class="goal-input-group">
                      <input id="goal-hours" v-model.number="goalHours" type="number" min="0" max="24"
                        class="goal-input" :placeholder="$t('trainingPlan.hoursPlaceholder')" />
                      <span class="goal-separator">:</span>
                      <input v-model.number="goalMinutes" type="number" min="0" max="59" class="goal-input"
                        :placeholder="$t('trainingPlan.minutesPlaceholder')" />
                      <span class="goal-separator">:</span>
                      <input v-model.number="goalSeconds" type="number" min="0" max="59" class="goal-input"
                        :placeholder="$t('trainingPlan.secondsPlaceholder')" />
                    </div>
                  </div>

                  <div class="goal-preview">
                    <h4>{{ $t('trainingPlan.targetTime') }}:</h4>
                    <div class="time-display">
                      {{ formatGoalTime() }}
                    </div>
                  </div>

                  <div class="goal-actions">
                    <button @click="saveGoal" class="btn btn-success">
                      <font-awesome-icon icon="save" />
                      {{ $t('trainingPlan.saveGoal') }}
                    </button>
                    <button v-if="currentGoal" @click="clearGoal" class="btn btn-outline-danger">
                      <font-awesome-icon icon="trash" />
                      {{ $t('trainingPlan.clearGoal') }}
                    </button>
                    <button @click="showGoalModal = false" class="btn btn-outline-secondary">
                      {{ $t('trainingPlan.cancel') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { trainingPlans, trainingZones } from '@/data/training-plans.js'

export default {
  name: 'TrainingPlan',
  metaInfo () {
    return {
      title: this.$t('trainingPlan.metaTitle') || 'Training Plans - Running Wild',
      meta: [
        {
          name: 'description',
          content: this.$t('trainingPlan.metaDescription') || 'Comprehensive training plans for runners of all levels. From 5K to marathon, get personalized training schedules with detailed weekly workouts.'
        },
        // Open Graph meta tags
        {
          property: 'og:title',
          content: this.$t('trainingPlan.metaTitle') || 'Training Plans - Running Wild'
        },
        {
          property: 'og:description',
          content: this.$t('trainingPlan.metaDescription') || 'Comprehensive training plans for runners of all levels. From 5K to marathon, get personalized training schedules with detailed weekly workouts.'
        },
        {
          property: 'og:type',
          content: 'website'
        },
        {
          property: 'og:url',
          content: this.currentUrl
        },
        {
          property: 'og:image',
          content: '/src/assets/default-running-avatar.png'
        },
        {
          property: 'og:image:alt',
          content: 'Running training plans'
        },
        {
          property: 'og:site_name',
          content: 'Running Wild'
        },
        // Twitter Card meta tags
        {
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          name: 'twitter:title',
          content: this.$t('trainingPlan.metaTitle') || 'Training Plans - Running Wild'
        },
        {
          name: 'twitter:description',
          content: this.$t('trainingPlan.metaDescription') || 'Comprehensive training plans for runners of all levels. From 5K to marathon, get personalized training schedules with detailed weekly workouts.'
        },
        {
          name: 'twitter:image',
          content: '/src/assets/default-running-avatar.png'
        },
        // Additional SEO meta tags
        {
          name: 'keywords',
          content: 'running training plans, marathon training, 5K training, half marathon training, running workouts, training schedule'
        },
        {
          name: 'author',
          content: 'Running Wild'
        },
        {
          name: 'robots',
          content: 'index, follow'
        }
      ],
      link: [
        {
          rel: 'canonical',
          href: this.currentUrl
        }
      ]
    }
  },
  data () {
    return {
      trainingPlans,
      trainingZones,
      selectedPlan: null,
      selectedWeek: null,
      showZonesModal: false,
      showGoalModal: false,
      showSchedule: false,
      goalHours: 0,
      goalMinutes: 0,
      goalSeconds: 0,
      currentGoal: null,
      planGoals: {} // Store goals for each plan
    }
  },
  computed: {
    currentUrl () {
      return window.location.href
    },
    adjustedTrainingPlan () {
      if (!this.selectedPlan || !this.currentGoal) {
        return this.selectedPlan
      }

      // Create a deep copy of the plan to avoid mutating original data
      const adjustedPlan = JSON.parse(JSON.stringify(this.selectedPlan))

      // Calculate goal pace (minutes per km) - this is the user's target race pace
      const goalTimeInMinutes = this.currentGoal.hours * 60 + this.currentGoal.minutes + this.currentGoal.seconds / 60
      const goalPacePerKm = goalTimeInMinutes / this.getDistanceInKm(this.selectedPlan.distance)

      // Calculate race pace (the actual target pace for race day)
      const racePace = this.formatPace(goalPacePerKm)

      // Adjust each week's training sessions
      adjustedPlan.weeks = adjustedPlan.weeks.map(week => {
        const adjustedWeek = { ...week }

        // Adjust each day's training
        Object.keys(adjustedWeek).forEach(dayKey => {
          if (dayKey !== 'week' && dayKey !== 'focus' && dayKey !== 'totalDistance') {
            const day = adjustedWeek[dayKey]
            if (!this.isRestDay(day) && day.metrics && day.intensity) {
              // Ensure metrics object exists
              if (!day.metrics) {
                day.metrics = {}
              }

              // Adjust pace based on user's goal pace (not original plan's target pace)
              if (day.metrics.pace && day.metrics.pace !== 'Race pace') {
                day.metrics.pace = this.calculateAdjustedPace(day.intensity, goalPacePerKm)
              } else if (day.metrics.pace === 'Race pace') {
                // Set the actual calculated race pace
                day.metrics.pace = racePace
              }

              // Adjust distance if needed
              if (day.metrics.distance) {
                day.metrics.distance = this.calculateAdjustedDistance(day.intensity, day.metrics.distance, goalPacePerKm)
              }

              // Adjust duration based on new pace and distance
              if (day.metrics.distance && day.metrics.pace) {
                day.metrics.duration = this.calculateDuration(day.metrics.distance, day.metrics.pace)
              }
            }
          }
        })

        return adjustedWeek
      })

      return adjustedPlan
    },
    adjustedSelectedWeek () {
      if (!this.selectedWeek || !this.currentGoal) {
        return this.selectedWeek
      }

      // Find the corresponding week in the adjusted plan
      const adjustedWeek = this.adjustedTrainingPlan.weeks.find(week => week.week === this.selectedWeek.week)
      return adjustedWeek || this.selectedWeek
    },
    hasGoalForCurrentPlan () {
      return this.selectedPlan && this.planGoals[this.selectedPlan.id]
    }
  },

  mounted () {
    // Initialize plan goals object
    this.trainingPlans.forEach(plan => {
      if (!this.planGoals[plan.id]) {
        this.planGoals[plan.id] = null
      }
    })

    // Load all saved goals for all plans when component mounts
    this.loadAllPlanGoals()
  },
  methods: {
    // Helper method to check if a day is a rest day
    isRestDay (day) {
      return day && (day.type === 'Rest' || day.type === 'Rest/Cross-Training')
    },

    selectPlan (plan) {
      this.selectedPlan = plan
      this.selectedWeek = plan.weeks[0]
      this.showSchedule = true
      this.loadGoalForPlan(plan.id)
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    selectWeek (week) {
      this.selectedWeek = week
      this.showSchedule = true
      // Scroll to schedule section
      const scheduleSection = document.querySelector('.schedule-section')
      if (scheduleSection) {
        scheduleSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    getPlanIcon (distance) {
      if (distance.includes('21.1')) return 'medal'
      if (distance.includes('42.2')) return 'trophy'
      return 'road'
    },
    getDaysOfWeek () {
      return {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday'
      }
    },
    getDayDate (dayName) {
      // Generate a sample date for the week (you can customize this)
      const dates = {
        monday: '15',
        tuesday: '16',
        wednesday: '17',
        thursday: '18',
        friday: '19',
        saturday: '20',
        sunday: '21'
      }
      return dates[dayName] || ''
    },
    formatGoalTime () {
      const hours = this.goalHours || 0
      const minutes = this.goalMinutes || 0
      const seconds = this.goalSeconds || 0

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    },
    saveGoal () {
      // Validate input
      if (this.goalHours < 0 || this.goalMinutes < 0 || this.goalSeconds < 0) {
        alert(this.$t('trainingPlan.invalidTime'))
        return
      }

      if (this.goalMinutes > 59 || this.goalSeconds > 59) {
        alert(this.$t('trainingPlan.invalidTime'))
        return
      }

      // Ensure we have a valid plan ID
      if (!this.selectedPlan || !this.selectedPlan.id) {
        alert('Invalid training plan selected.')
        return
      }

      // Save goal to localStorage or send to backend
      const goal = {
        planId: this.selectedPlan.id,
        planName: this.selectedPlan.name,
        targetTime: this.formatGoalTime(),
        hours: this.goalHours,
        minutes: this.goalMinutes,
        seconds: this.goalSeconds,
        timestamp: new Date().toISOString()
      }

      // Store in localStorage for now (you can replace with API call)
      localStorage.setItem(`trainingGoal_${this.selectedPlan.id}`, JSON.stringify(goal))

      // Update current goal and plan goals
      this.currentGoal = goal
      this.planGoals[this.selectedPlan.id] = goal

      // Force reactive update by triggering a re-computation
      this.$nextTick(() => {
        // Update selected week to trigger reactive updates
        if (this.selectedWeek) {
          const weekIndex = this.adjustedTrainingPlan.weeks.findIndex(w => w.week === this.selectedWeek.week)
          if (weekIndex !== -1) {
            this.selectedWeek = this.adjustedTrainingPlan.weeks[weekIndex]
          }
        }
      })

      // Show success message
      alert(this.$t('trainingPlan.goalSaved'))

      // Close modal
      this.showGoalModal = false

      // Reset form
      this.goalHours = 0
      this.goalMinutes = 0
      this.goalSeconds = 0
    },
    clearGoal () {
      // Clear goal for current plan
      if (this.selectedPlan && this.selectedPlan.id) {
        localStorage.removeItem(`trainingGoal_${this.selectedPlan.id}`)
        this.currentGoal = null
        this.planGoals[this.selectedPlan.id] = null

        // Show success message
        alert(this.$t('trainingPlan.goalCleared'))

        // Close modal
        this.showGoalModal = false

        // Reset form
        this.goalHours = 0
        this.goalMinutes = 0
        this.goalSeconds = 0
      } else {
        alert('Invalid training plan selected.')
      }
    },
    getDistanceInKm (distanceString) {
      // Extract distance from string like "21.1km" or "42.2km"
      const match = distanceString.match(/(\d+\.?\d*)/)
      return match ? parseFloat(match[1]) : 0
    },
    formatPace (pacePerKm) {
      // Format pace as MM:SS from minutes per km
      const minutes = Math.floor(pacePerKm)
      const seconds = Math.round((pacePerKm - minutes) * 60)
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    calculateRacePace () {
      // Calculate the actual race pace based on goal
      if (!this.currentGoal || !this.selectedPlan) {
        return null
      }

      const goalTimeInMinutes = this.currentGoal.hours * 60 + this.currentGoal.minutes + this.currentGoal.seconds / 60
      const goalPacePerKm = goalTimeInMinutes / this.getDistanceInKm(this.selectedPlan.distance)

      return this.formatPace(goalPacePerKm)
    },
    parseTargetTimeToPace (targetTime) {
      // Parse target time like "1:45-2:30" or "3:30-4:00" to get average pace per km
      if (!targetTime) return 6.0 // Default pace if no target time

      // Extract time range (e.g., "1:45-2:30" -> ["1:45", "2:30"])
      const timeRange = targetTime.split('-')
      if (timeRange.length !== 2) return 6.0

      // Parse both times and calculate average
      const time1 = this.parseTimeString(timeRange[0].trim())
      const time2 = this.parseTimeString(timeRange[1].trim())

      if (!time1 || !time2) return 6.0

      // Calculate average time in minutes
      const avgTimeMinutes = (time1 + time2) / 2

      // Calculate pace per km (assuming half marathon = 21.1km)
      const distance = this.getDistanceInKm(this.selectedPlan.distance)
      const pacePerKm = avgTimeMinutes / distance

      return pacePerKm
    },
    parseTimeString (timeStr) {
      // Parse time string like "1:45" or "2:30" to minutes
      const parts = timeStr.split(':')
      if (parts.length !== 2) return null

      const hours = parseInt(parts[0])
      const minutes = parseInt(parts[1])

      if (isNaN(hours) || isNaN(minutes)) return null

      return hours * 60 + minutes
    },
    parsePaceTimeString (timeStr) {
      // Parse pace time string like "4:49" or "6:30" to minutes per km
      // For pace, "4:49" means 4 minutes 49 seconds per km
      const parts = timeStr.split(':')
      if (parts.length !== 2) return null

      const minutes = parseInt(parts[0])
      const seconds = parseInt(parts[1])

      if (isNaN(minutes) || isNaN(seconds)) return null

      return minutes + (seconds / 60)
    },
    calculateAdjustedPace (intensity, originalPacePerKm) {
      // Adjust pace based on intensity level and original plan's target pace
      // These multipliers are based on common training zone percentages
      const intensityMultipliers = {
        'Easy': 1.15, // 15% slower than original target pace (Zone 2)
        'Recovery': 1.25, // 25% slower than original target pace (Zone 1)
        'Moderate': 1.05, // 5% slower than original target pace (Zone 3)
        'Hard': 0.85, // 15% faster than original target pace (Zone 4-5)
        'Tempo': 0.92, // 8% faster than original target pace (Zone 3-4)
        'Race': 1.0 // Exactly original target pace
      }

      const multiplier = intensityMultipliers[intensity] || 1.0
      const adjustedPace = originalPacePerKm * multiplier

      // Format pace with a range for better training guidance
      const basePace = this.formatPace(adjustedPace)
      const paceMinutes = Math.floor(adjustedPace)
      const paceSeconds = Math.round((adjustedPace - paceMinutes) * 60)

      // Create a range of ±5 seconds for training flexibility
      const lowerSeconds = Math.max(0, paceSeconds - 5)
      const upperSeconds = Math.min(59, paceSeconds + 5)

      if (lowerSeconds === upperSeconds) {
        return basePace
      } else {
        const lowerPace = `${paceMinutes}:${lowerSeconds.toString().padStart(2, '0')}`
        const upperPace = `${paceMinutes}:${upperSeconds.toString().padStart(2, '0')}`
        return `${lowerPace}-${upperPace}/km`
      }
    },
    calculateAdjustedDistance (intensity, originalDistance, originalPacePerKm) {
      // Adjust distance based on intensity and original plan's target pace
      // These multipliers are based on training principles for different intensities
      const distanceMultipliers = {
        'Easy': 0.9, // 90% of original distance (slightly shorter for easier pace)
        'Recovery': 0.7, // 70% of original distance (shorter for recovery)
        'Moderate': 1.0, // Keep original distance
        'Hard': 1.1, // 110% of original distance (slightly longer for hard sessions)
        'Tempo': 1.05, // 105% of original distance
        'Race': 1.0 // Keep original distance for race
      }

      const multiplier = distanceMultipliers[intensity] || 1.0
      // Extract numeric value from distance string (e.g., "5km" -> 5, "4km total" -> 4)
      const distanceMatch = originalDistance.match(/(\d+\.?\d*)/)
      const distanceValue = distanceMatch ? parseFloat(distanceMatch[1]) : 0
      const adjustedDistance = distanceValue * multiplier

      // Format distance with one decimal place for precision
      const formattedDistance = adjustedDistance.toFixed(1)
      // Remove trailing .0 if it's a whole number
      const cleanDistance = formattedDistance.endsWith('.0') ? formattedDistance.slice(0, -2) : formattedDistance

      return `${cleanDistance}km`
    },
    calculateDuration (distance, pace) {
      // Calculate duration based on distance and pace
      // Extract numeric value from distance string (e.g., "5km" -> 5, "4km total" -> 4)
      const distanceMatch = distance.match(/(\d+\.?\d*)/)
      const distanceKm = distanceMatch ? parseFloat(distanceMatch[1]) : 0

      // Parse pace (now in format "5:30" or "6:00-6:30")
      const pacePerKm = this.parsePaceToMinutes(pace)

      const totalMinutes = distanceKm * pacePerKm
      const hours = Math.floor(totalMinutes / 60)
      const minutes = Math.floor(totalMinutes % 60)

      if (hours > 0) {
        return `${hours}h ${minutes}m`
      } else {
        return `${minutes}m`
      }
    },
    parsePaceToMinutes (pace) {
      // Parse pace string like "5:30" or "6:00-6:30" to minutes per km
      if (!pace) return 6.0 // Default pace

      // Remove "/km" suffix if present
      const cleanPace = pace.replace('/km', '')

      // Handle range format like "6:00-6:30"
      if (cleanPace.includes('-')) {
        const [pace1, pace2] = cleanPace.split('-').map(p => p.trim())
        const minutes1 = this.parsePaceTimeString(pace1)
        const minutes2 = this.parsePaceTimeString(pace2)
        if (minutes1 && minutes2) {
          return (minutes1 + minutes2) / 2
        }
      }

      // Handle single pace like "5:30"
      const minutes = this.parsePaceTimeString(cleanPace)
      return minutes || 6.0
    },
    loadGoalForPlan (planId) {
      // Load goal for specific plan from localStorage
      const savedGoal = localStorage.getItem(`trainingGoal_${planId}`)
      if (savedGoal) {
        try {
          const goal = JSON.parse(savedGoal)
          this.currentGoal = goal
          this.planGoals[planId] = goal
        } catch (error) {
          console.error('Error parsing saved goal:', error)
          this.currentGoal = null
          this.planGoals[planId] = null
        }
      } else {
        this.currentGoal = null
        this.planGoals[planId] = null
      }
    },
    loadAllPlanGoals () {
      // Load all saved goals for all plans
      this.trainingPlans.forEach(plan => {
        const savedGoal = localStorage.getItem(`trainingGoal_${plan.id}`)
        if (savedGoal) {
          try {
            this.planGoals[plan.id] = JSON.parse(savedGoal)
          } catch (error) {
            console.error(`Error parsing saved goal for plan ${plan.id}:`, error)
            this.planGoals[plan.id] = null
          }
        }
      })
    },
    downloadTrainingPlan () {
      if (!this.selectedPlan) {
        alert(this.$t('trainingPlan.downloadError'))
        return
      }

      // Get the adjusted training plan (with goal modifications if any)
      const planToExport = this.adjustedTrainingPlan
      const planName = this.selectedPlan.name
      const goalInfo = this.currentGoal ? ` (Goal: ${this.currentGoal.targetTime})` : ''

      // Create CSV content
      let csvContent = `"${planName}${goalInfo}"\n\n`

      // Add plan overview
      csvContent += `"${this.$t('trainingPlan.planOverview')}"\n`
      csvContent += `"Name","${planName}"\n`
      csvContent += `"Description","${this.selectedPlan.description}"\n`
      csvContent += `"Duration","${this.selectedPlan.duration} Weeks"\n`
      csvContent += `"Distance","${this.selectedPlan.distance}"\n`
      csvContent += `"Target Time","${this.selectedPlan.targetTime}"\n`
      if (this.currentGoal) {
        csvContent += `"${this.$t('trainingPlan.goalTime')}","${this.currentGoal.targetTime}"\n`
        csvContent += `"${this.$t('trainingPlan.goalStatus')}","${this.$t('trainingPlan.active')}"\n`
      }
      csvContent += `\n`

      // Add weekly schedules
      planToExport.weeks.forEach((week, weekIndex) => {
        csvContent += `"Week ${week.week}: ${week.focus}"\n`
        csvContent += `"Day","Type","Description","Distance","Pace","Duration","Intensity","Notes"\n`

        // Get days of week
        const daysOfWeek = this.getDaysOfWeek()
        Object.keys(daysOfWeek).forEach(dayName => {
          const day = week[dayName]
          if (day) {
            const dayDisplay = this.$t(`trainingPlan.days.${dayName}`)
            const type = day.type || 'Rest'
            const isRest = this.isRestDay(day)
            const description = day.description || (isRest ? this.$t('trainingPlan.recoveryAndRestDay') : '')
            const distance = (day.metrics && day.metrics.distance) || (isRest ? this.$t('trainingPlan.noTraining') : '')
            const pace = (day.metrics && day.metrics.pace) || (isRest ? this.$t('trainingPlan.noTraining') : '')
            const duration = (day.metrics && day.metrics.duration) || (isRest ? this.$t('trainingPlan.noTraining') : '')
            const intensity = day.intensity || (isRest ? 'Rest' : '')
            const notes = day.notes || '-'

            csvContent += `"${dayDisplay}","${type}","${description}","${distance}","${pace}","${duration}","${intensity}","${notes}"\n`
          }
        })

        csvContent += `\n`
      })

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `${planName.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.csv`

      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Show success message
      alert(this.$t('trainingPlan.downloadSuccess').replace('{planName}', planName))
    },

    startTraining () {
      // Implementation for starting training
      alert(this.$t('trainingPlan.startTrainingMessage') || 'Starting your training plan!')
    },

    sharePlan () {
      if (!this.selectedPlan) {
        alert(this.$t('trainingPlan.shareError') || 'No plan selected to share')
        return
      }

      // Check if Web Share API is available
      if (navigator.share) {
        const shareData = {
          title: this.selectedPlan.name,
          text: `${this.selectedPlan.name} - ${this.selectedPlan.description}`,
          url: window.location.href
        }

        navigator.share(shareData)
          .catch((error) => {
            console.log('Error sharing:', error)
            this.fallbackShare()
          })
      } else {
        this.fallbackShare()
      }
    },

    fallbackShare () {
      // Fallback for browsers that don't support Web Share API
      const url = window.location.href
      const text = `${this.selectedPlan.name} - ${this.selectedPlan.description}`

      // Try to copy to clipboard
      if (navigator.clipboard) {
        navigator.clipboard.writeText(`${text}\n${url}`)
          .then(() => {
            alert(this.$t('trainingPlan.shareCopied') || 'Link copied to clipboard!')
          })
          .catch(() => {
            // Fallback to prompt
            prompt(this.$t('trainingPlan.sharePrompt') || 'Copy this link:', url)
          })
      } else {
        // Final fallback
        prompt(this.$t('trainingPlan.sharePrompt') || 'Copy this link:', url)
      }
    },

    closeScheduleView () {
      // Go back to plan selection (same as back button)
      this.selectedPlan = null
      this.showSchedule = false
      this.selectedWeek = null
    }

  }
}
</script>

<style scoped>
.training-plan-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Header Section - More Compact */
.header-section {
  padding: 2rem 0 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.header-content {
  text-align: center;
  color: white;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
}

.title-icon {
  font-size: 2rem;
  color: #ffd700;
}

.page-subtitle {
  font-size: 1rem;
  opacity: 0.9;
  max-width: 500px;
  margin: 0 auto;
}

/* Plans Section - More Compact */
.plans-section {
  padding: 1.5rem 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Plans Grid - More Compact */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.plan-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.plan-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.plan-card--featured {
  border: 2px solid #ffd700;
  transform: scale(1.02);
}

.plan-card--featured:hover {
  transform: scale(1.02) translateY(-5px);
}

.plan-card__header {
  text-align: center;
  margin-bottom: 1.5rem;
  position: relative;
}

.plan-card__badge {
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  background: linear-gradient(45deg, #ffd700, #ffed4e);
  color: #333;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.plan-card__icon {
  font-size: 2.5rem;
  color: #667eea;
  margin-bottom: 0.8rem;
}

.plan-card__title {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.4rem;
  color: #333;
}

.plan-card__description {
  color: #666;
  line-height: 1.5;
  font-size: 0.9rem;
}

.plan-card__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.8rem;
  margin-bottom: 1.5rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.8rem;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
}

.stat-icon {
  color: #667eea;
  font-size: 1rem;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-weight: 700;
  color: #333;
  font-size: 1rem;
}

.stat-label {
  font-size: 0.7rem;
  color: #666;
  text-transform: uppercase;
}

.plan-card__footer {
  margin-top: auto;
}

.plan-goal-status {
  margin-top: 0.8rem;
  padding-top: 0.8rem;
  border-top: 1px solid #e9ecef;
}

.plan-goal-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  color: #155724;
  font-weight: 600;
}

.plan-goal-indicator svg {
  color: #28a745;
  font-size: 0.7rem;
}

.plan-goal-time {
  font-family: 'Courier New', monospace;
  font-weight: 700;
}

/* Plan Detail - More Compact */
.plan-detail {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.back-section {
  margin-bottom: 1.5rem;
}

.plan-overview {
  margin-bottom: 2rem;
}

.plan-overview__header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: white;
}

.plan-overview__icon {
  font-size: 3rem;
  color: #ffd700;
}

.plan-overview__title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.4rem;
}

.plan-overview__description {
  font-size: 1rem;
  opacity: 0.9;
  margin-bottom: 0.8rem;
}

.plan-overview__meta {
  display: flex;
  gap: 1.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 600;
  font-size: 0.9rem;
}

/* Zones Section - More Compact */
.zones-section {
  margin-bottom: 2rem;
}

.zones-info-btn {
  background: #667eea;
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
  width: 100%;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(102, 126, 234, 0.2);
}

.zones-info-btn:hover {
  background: #5a67d8;
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(102, 126, 234, 0.3);
}

.zones-info-btn .fa-info-circle {
  font-size: 1rem;
}

/* Modal Styles - More Compact */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.modal-title .fa-chart-line {
  color: #667eea;
  font-size: 1.3rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  transition: color 0.3s ease;
}

.modal-close:hover {
  color: #333;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex-grow: 1;
}

.zones-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.2rem;
}

.zone-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.2rem;
  border-left: 4px solid #667eea;
  transition: all 0.3s ease;
}

.zone-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.zone-card--zone1 {
  border-left-color: #28a745;
}

.zone-card--zone2 {
  border-left-color: #17a2b8;
}

.zone-card--zone3 {
  border-left-color: #ffc107;
}

.zone-card--zone4 {
  border-left-color: #fd7e14;
}

.zone-card--zone5 {
  border-left-color: #dc3545;
}

.zone-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
}

.zone-card__title {
  font-weight: 700;
  color: #333;
  margin: 0;
}

.zone-card__pace {
  background: #667eea;
  color: white;
  padding: 0.2rem 0.6rem;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 600;
}

.zone-card__description {
  color: #666;
  margin-bottom: 0.8rem;
  line-height: 1.4;
  font-size: 0.9rem;
}

.zone-card__details {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.zone-detail {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #555;
}

/* Schedule Section - More Compact */
.schedule-section {
  margin-bottom: 2rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  position: relative;
}

.schedule-close-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.schedule-close-btn:hover {
  background: #f8f9fa;
  color: #333;
}

.week-schedule {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.week-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f8f9fa;
}

.week-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
}

.week-stats {
  display: flex;
  gap: 1rem;
}

.week-stat {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #666;
  font-size: 0.9rem;
}

/* Action Section - More Compact */
.action-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #dee2e6;
}

.action-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.action-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
}

.action-subtitle {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-button-group {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.action-btn {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem 1.2rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}

.action-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.action-btn:hover::before {
  left: 100%;
}

.action-btn--primary {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
}

.action-btn--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4);
}

.action-btn--secondary {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.action-btn--secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

.action-btn--tertiary {
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.action-btn--tertiary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
}

.action-btn__icon {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.action-btn__content {
  flex: 1;
  text-align: left;
}

.action-btn__title {
  display: block;
  font-weight: 700;
  margin-bottom: 0.2rem;
}

.action-btn__subtitle {
  display: block;
  font-size: 0.8rem;
  opacity: 0.9;
  font-weight: 400;
}

.action-btn__arrow {
  font-size: 0.9rem;
  opacity: 0.8;
  transition: transform 0.3s ease;
}

.action-btn:hover .action-btn__arrow {
  transform: translateX(4px);
}

/* Goal Status - More Compact */
.goal-status {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border: 1px solid #c3e6cb;
  border-radius: 10px;
  padding: 1rem;
  margin-top: 1rem;
}

.goal-status__icon {
  color: #28a745;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.goal-status__content {
  flex: 1;
}

.goal-status__title {
  display: block;
  font-weight: 700;
  color: #155724;
  margin-bottom: 0.2rem;
  font-size: 0.9rem;
}

.goal-status__time {
  display: block;
  font-family: 'Courier New', monospace;
  font-weight: 700;
  color: #155724;
  font-size: 1.1rem;
}

.goal-status__pace {
  display: block;
  color: #155724;
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

.goal-status__edit {
  background: none;
  border: none;
  color: #28a745;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.goal-status__edit:hover {
  background: rgba(40, 167, 69, 0.1);
  transform: scale(1.1);
}

/* Week Selector - More Compact */
.week-selector {
  margin-bottom: 1.5rem;
}

.week-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.week-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.8rem 0.6rem;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 60px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.week-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #4a90e2;
}

.week-button--active {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  border-color: #4a90e2;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
}

.week-icon {
  font-size: 1rem;
}

.week-number {
  font-weight: 700;
  font-size: 0.8rem;
}

/* Training Table - More Compact */
.training-table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.training-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.table-header-day,
.table-header-type,
.table-header-description,
.table-header-metrics,
.table-header-intensity,
.table-header-notes {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 0.8rem;
  text-align: left;
  font-weight: 700;
  color: #333;
  border-bottom: 2px solid #dee2e6;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  border-bottom: 1px solid #f8f9fa;
  transition: background 0.3s ease;
}

.table-row:hover {
  background: #f8f9fa;
}

.table-row--monday { border-left: 4px solid #e74c3c; }
.table-row--tuesday { border-left: 4px solid #f39c12; }
.table-row--wednesday { border-left: 4px solid #f1c40f; }
.table-row--thursday { border-left: 4px solid #27ae60; }
.table-row--friday { border-left: 4px solid #3498db; }
.table-row--saturday { border-left: 4px solid #9b59b6; }
.table-row--sunday { border-left: 4px solid #e67e22; }

.table-cell-day,
.table-cell-type,
.table-cell-description,
.table-cell-metrics,
.table-cell-intensity,
.table-cell-notes {
  padding: 0.8rem;
  vertical-align: top;
}

.table-cell-day {
  font-weight: 600;
  color: #333;
  width: 100px;
}

.day-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.day-name {
  font-weight: 700;
  font-size: 0.9rem;
}

.session-type-badge {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  padding: 0.3rem 0.6rem;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 600;
  display: inline-block;
}

.rest-badge {
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
  color: white;
  padding: 0.3rem 0.6rem;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.rest-badge--cross-training {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
}

.session-description {
  color: #333;
  line-height: 1.4;
  font-size: 0.85rem;
}

.rest-description {
  color: #666;
  font-style: italic;
  font-size: 0.85rem;
}

.session-metrics {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8rem;
  color: #555;
}

.adjusted-metric {
  color: #28a745;
  font-weight: 600;
}

.metric-adjustment-indicator {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #28a745;
  font-size: 0.7rem;
  font-weight: 600;
  margin-top: 0.2rem;
}

.rest-metrics {
  color: #999;
  font-style: italic;
  font-size: 0.8rem;
}

.rest-text {
  color: #999;
  font-style: italic;
}

.intensity-badge {
  padding: 0.3rem 0.6rem;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 600;
  display: inline-block;
}

.intensity-badge--easy {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
}

.intensity-badge--moderate {
  background: linear-gradient(135deg, #ffc107 0%, #ffb347 100%);
  color: #333;
}

.intensity-badge--hard {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
}

.intensity-badge--very-hard {
  background: linear-gradient(135deg, #6f42c1 0%, #5a32a3 100%);
  color: white;
}

.rest-intensity {
  color: #999;
  font-style: italic;
  font-size: 0.8rem;
}

.session-notes {
  display: flex;
  align-items: flex-start;
  gap: 0.3rem;
  color: #666;
  font-size: 0.8rem;
  line-height: 1.3;
}

.no-notes {
  color: #ccc;
  font-style: italic;
  font-size: 0.8rem;
}

.no-notes-text {
  color: #ccc;
}

/* Mobile Training Cards - More Compact */
.mobile-training-cards {
  display: none;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
}

.training-day-card {
  background: white;
  border-radius: 10px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #e9ecef;
  transition: all 0.3s ease;
}

.training-day-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.training-day-card--monday { border-left-color: #e74c3c; }
.training-day-card--tuesday { border-left-color: #f39c12; }
.training-day-card--wednesday { border-left-color: #f1c40f; }
.training-day-card--thursday { border-left-color: #27ae60; }
.training-day-card--friday { border-left-color: #3498db; }
.training-day-card--saturday { border-left-color: #9b59b6; }
.training-day-card--sunday { border-left-color: #e67e22; }

.day-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
}

.day-card-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 700;
  color: #333;
  font-size: 0.9rem;
}

.day-icon {
  color: #4a90e2;
  font-size: 0.8rem;
}

.day-card-type {
  flex-shrink: 0;
}

.mobile-badge {
  font-size: 0.6rem;
  padding: 0.2rem 0.4rem;
}

.day-card-content {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.day-card-description {
  color: #333;
  line-height: 1.4;
  font-size: 0.85rem;
}

.day-card-metrics {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.mobile-metrics {
  gap: 0.3rem;
}

.day-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f8f9fa;
}

.day-card-intensity {
  flex-shrink: 0;
}

.mobile-intensity {
  font-size: 0.6rem;
  padding: 0.2rem 0.4rem;
}

.day-card-notes {
  display: flex;
  align-items: flex-start;
  gap: 0.3rem;
  color: #666;
  font-size: 0.75rem;
  line-height: 1.3;
  flex: 1;
  margin-left: 0.5rem;
}

/* Goal Form - More Compact */
.goal-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.goal-description {
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
}

.goal-input-section {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.goal-label {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.goal-input-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.goal-input {
  width: 80px;
  padding: 0.6rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  text-align: center;
  transition: all 0.3s ease;
}

.goal-input:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.goal-separator {
  font-weight: 700;
  color: #333;
  font-size: 1.2rem;
}

.goal-preview {
  text-align: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 10px;
  border: 2px solid #e9ecef;
}

.goal-preview h4 {
  margin-bottom: 0.5rem;
  color: #333;
  font-size: 0.9rem;
}

.time-display {
  font-family: 'Courier New', monospace;
  font-size: 2rem;
  font-weight: 700;
  color: #4a90e2;
}

.goal-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
}

/* Responsive Design - More Compact */
@media (max-width: 768px) {
  .training-plan-container {
    min-height: 100vh;
  }

  .header-section {
    padding: 1.5rem 0 1rem;
  }

  .page-title {
    font-size: 2rem;
    gap: 0.6rem;
  }

  .title-icon {
    font-size: 1.5rem;
  }

  .page-subtitle {
    font-size: 0.9rem;
    max-width: 400px;
  }

  .plans-section {
    padding: 1rem 0;
  }

  .plans-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .plan-card {
    padding: 1.2rem;
    border-radius: 12px;
  }

  .plan-card__header {
    margin-bottom: 1.2rem;
  }

  .plan-card__icon {
    font-size: 2rem;
    margin-bottom: 0.6rem;
  }

  .plan-card__title {
    font-size: 1.2rem;
  }

  .plan-card__description {
    font-size: 0.85rem;
  }

  .plan-card__stats {
    gap: 0.6rem;
    margin-bottom: 1.2rem;
  }

  .stat-item {
    padding: 0.6rem;
  }

  .stat-icon {
    font-size: 0.9rem;
  }

  .stat-value {
    font-size: 0.9rem;
  }

  .stat-label {
    font-size: 0.65rem;
  }

  .plan-detail {
    padding: 1.2rem;
    border-radius: 12px;
  }

  .plan-overview__header {
    padding: 1.2rem;
    gap: 1rem;
  }

  .plan-overview__icon {
    font-size: 2.5rem;
  }

  .plan-overview__title {
    font-size: 1.5rem;
  }

  .plan-overview__description {
    font-size: 0.9rem;
  }

  .plan-overview__meta {
    gap: 1rem;
  }

  .meta-item {
    font-size: 0.8rem;
  }

  .action-section {
    padding: 1.2rem;
  }

  .action-title {
    font-size: 1.2rem;
  }

  .action-subtitle {
    font-size: 0.85rem;
  }

  .action-button-group {
    flex-direction: column;
    gap: 0.8rem;
  }

  .action-btn {
    min-width: auto;
    padding: 0.8rem 1rem;
  }

  .action-btn__title {
    font-size: 0.85rem;
  }

  .action-btn__subtitle {
    font-size: 0.75rem;
  }

  .goal-status {
    padding: 0.8rem;
    gap: 0.8rem;
  }

  .goal-status__title {
    font-size: 0.85rem;
  }

  .goal-status__time {
    font-size: 1rem;
  }

  .goal-status__pace {
    font-size: 0.75rem;
  }

  .week-buttons {
    gap: 0.4rem;
  }

  .week-button {
    padding: 0.6rem 0.4rem;
    min-width: 50px;
  }

  .week-icon {
    font-size: 0.9rem;
  }

  .week-number {
    font-size: 0.7rem;
  }

  .training-table-container {
    border-radius: 8px;
  }

  .training-table {
    font-size: 0.8rem;
  }

  .table-header-day,
  .table-header-type,
  .table-header-description,
  .table-header-metrics,
  .table-header-intensity,
  .table-header-notes {
    padding: 0.6rem;
    font-size: 0.7rem;
  }

  .table-cell-day,
  .table-cell-type,
  .table-cell-description,
  .table-cell-metrics,
  .table-cell-intensity,
  .table-cell-notes {
    padding: 0.6rem;
  }

  .table-cell-day {
    width: 80px;
  }

  .day-name {
    font-size: 0.8rem;
  }

  .session-type-badge,
  .rest-badge {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
  }

  .session-description,
  .rest-description {
    font-size: 0.8rem;
  }

  .metric-item {
    font-size: 0.75rem;
  }

  .intensity-badge {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
  }

  .session-notes {
    font-size: 0.75rem;
  }

  .mobile-training-cards {
    display: flex;
  }

  .training-day-card {
    padding: 0.8rem;
  }

  .day-card-title {
    font-size: 0.85rem;
  }

  .day-card-description {
    font-size: 0.8rem;
  }

  .day-card-notes {
    font-size: 0.7rem;
  }

  .modal-content {
    width: 95%;
    max-height: 90vh;
  }

  .modal-header {
    padding: 1rem 1.2rem;
  }

  .modal-title {
    font-size: 1.3rem;
  }

  .modal-body {
    padding: 1.2rem;
  }

  .goal-input {
    width: 60px;
    padding: 0.5rem;
    font-size: 0.9rem;
  }

  .time-display {
    font-size: 1.5rem;
  }

  .goal-actions {
    gap: 0.8rem;
  }
}

/* Mobile Full Viewport Schedule */
@media (max-width: 768px) {
  .schedule-section {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: white;
    z-index: 1000;
    overflow-y: auto;
    margin: 0;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    animation: slideInUp 0.3s ease-out;
  }

  .mobile-back-btn {
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e9ecef;
  }

  .mobile-back-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.8rem 1.2rem;
    font-size: 0.9rem;
    font-weight: 600;
    border-radius: 8px;
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    color: #495057;
    transition: all 0.3s ease;
  }

  .mobile-back-button:hover {
    background: #e9ecef;
    border-color: #6c757d;
    color: #343a40;
    transform: translateY(-1px);
  }

  @keyframes slideInUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .schedule-section .week-schedule {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .schedule-section .training-table-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin: 0;
    border-radius: 0;
    box-shadow: none;
    border: none;
  }

  .desktop-table {
    display: none;
  }

  .mobile-training-cards {
    display: flex;
  }
}

/* Desktop Table View */
@media (min-width: 769px) {
  .mobile-training-cards {
    display: none;
  }

  .mobile-back-btn {
    display: none;
  }

  .desktop-table {
    display: table;
  }
}
</style>
